import { get } from "lodash";
import { call, put, select } from "redux-saga/effects";

import {
  ApiTriggerType,
  ResourceTypeEnum,
  UIErrorType,
} from "@superblocksteam/shared";
import type { Agent } from "@superblocksteam/shared";

import { HttpError, ROOT } from "core/store/utils/types";

import {
  makeSingleUseSocketConnection,
  type StdISocketRPCClient,
} from "../../../../legacy/api/ISocketRPC";
import { ERROR_CODES } from "../../../../legacy/constants/ApiConstants";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
} from "../../../../legacy/constants/ReduxActionConstants";
import { getIsOrgSwitched } from "../../../../legacy/selectors/usersSelectors";
import { getOpaAgents } from "../../../../legacy/utils/getOpaAgents";
import { resetCachedCurrentBranch } from "../../../../pages/Repositories/utils";
import logger, { stringifyError } from "../../../../utils/logger";
import { findAndUnMarshalProtoBlocks } from "../../../../utils/marshalProto";
import { sendErrorUINotification } from "../../../../utils/notification";
import {
  getShouldSignAndVerify,
  verifyResources,
} from "../../../../utils/resource-signing";
import { createSaga, SagaType } from "../../../utils/saga";
import { selectOnlyOrganization } from "../../organizations";
import { orgIsOnPremise } from "../../organizations/utils";
import { fetchApiPermissions } from "../actions";
import { getApiV3 } from "../client";
import slice, { isApiDtoWithPb, type ApiDtoWithPb } from "../slice";
import { getV2ApiId } from "../utils/getApiIdAndName";

interface FetchV2ApiPayload {
  apiId: string;
  branch?: string;
  canCrash?: boolean;
}

export function* fetchV2ApiInternal({
  apiId,
  branch,
  canCrash,
}: FetchV2ApiPayload) {
  // This is called in workflow/job only to fetch workflow/job
  // get profile given mode
  const organization: ReturnType<typeof selectOnlyOrganization> = yield select(
    selectOnlyOrganization,
  );
  const agents: Agent[] = yield call(getOpaAgents);

  let result: ApiDtoWithPb;
  let shouldVerifySignature = false;
  let rpcClient: undefined | StdISocketRPCClient;
  try {
    if (orgIsOnPremise(organization)) {
      const isSigningEnabled: boolean = yield call(
        getShouldSignAndVerify,
        agents,
      );
      if (isSigningEnabled) {
        if (agents.length === 0) {
          const error = Object.assign(new Error("No OPA agents found"), {
            code: ERROR_CODES.NO_AGENTS,
          });
          throw error;
        }
        rpcClient = yield call(
          makeSingleUseSocketConnection,
          agents,
          organization,
        );
        shouldVerifySignature = true;
      }
    }

    if (rpcClient) {
      const response: Awaited<ReturnType<typeof rpcClient.call.v3.api.get>> =
        yield call(rpcClient.call.v3.api.get, { apiId, branchName: branch });
      if (response.responseMeta.status !== 200) {
        const status = response.responseMeta.status;
        throw new HttpError(
          status,
          !status || status >= 500,
          response.responseMeta.message,
        );
      }
      if (!isApiDtoWithPb(response.data)) {
        throw new Error("API response did not include an API definition");
      }
      result = response.data;
    } else {
      result = yield call(getApiV3, {
        apiId,
        ...(branch ? { branch } : {}),
        errorHandlingOptions: {
          notifyOnError: false,
          onError: (error) => {
            throw error;
          },
        },
      });
    }

    // If it's a workflow or a scheduled job, fetch permissions
    if (
      result.triggerType === ApiTriggerType.WORKFLOW ||
      result.triggerType === ApiTriggerType.SCHEDULE
    ) {
      yield put(
        fetchApiPermissions(
          apiId,
          result.triggerType === ApiTriggerType.WORKFLOW
            ? ResourceTypeEnum.WORKFLOWS
            : ResourceTypeEnum.SCHEDULED_JOBS,
        ),
      );
    }
  } catch (error: any) {
    resetCachedCurrentBranch(apiId);
    const code = get(error, "code", ERROR_CODES.SERVER_ERROR);
    if (canCrash) {
      yield put({
        type: ReduxActionErrorTypes.FETCH_WORKFLOW_OR_JOB_ERROR,
        payload: {
          error,
        },
      });
      logger.error(
        `Fetch api failed:\n  code: ${code}, error:\n  ${stringifyError(
          error,
        )}\n`,
        {
          superblocks_ui_error_type:
            code !== 404
              ? UIErrorType.CRASH_APP_ERROR
              : UIErrorType.SERVER_ERROR_4XX,
          superblocks_ui_error_code: code as number | undefined,
        },
      );

      // crash worflow page with error page if can't fetch workflow
      yield put({
        type: ReduxActionTypes.SAFE_CRASH_SUPERBLOCKS_REQUEST,
        payload: {
          code,
        },
      });
    }
    throw error;
  } finally {
    rpcClient?.close();
  }

  if (shouldVerifySignature) {
    const isOrgSwitched: boolean = yield select(getIsOrgSwitched);
    if (!isOrgSwitched) {
      try {
        yield call(verifyResources, {
          resources: [{ api: result.apiPb }],
          agents,
          organization,
          branchName: branch,
        });
      } catch (e: any) {
        if (canCrash) {
          yield put({
            type: ReduxActionErrorTypes.FETCH_WORKFLOW_OR_JOB_ERROR,
            payload: {
              error: e,
            },
          });
          // crash worflow page with error page if can't fetch workflow
          yield put({
            type: ReduxActionTypes.SAFE_CRASH_SUPERBLOCKS_REQUEST,
            payload: {
              code: ERROR_CODES.VERIFY_RESOURCES_FAILED,
            },
          });
        } else {
          sendErrorUINotification({
            message: "Failed to verify resource",
          });
        }
        throw new Error("Could not verify resource");
      }
    }
  }
  yield put({
    type: ReduxActionTypes.UPDATE_LAST_SUCCESSFUL_WRITE,
    payload: result.updated,
  });

  return result;
}

export const fetchV2ApiSaga = createSaga(fetchV2ApiInternal, "fetchV2ApiSaga", {
  sliceName: "apis",
  type: SagaType.Throttled,
  delay: 1500,
});

slice.saga(fetchV2ApiSaga, {
  start(state) {
    state.loading[ROOT] = true;
  },
  success(state, { payload }) {
    if (!payload) return;

    findAndUnMarshalProtoBlocks(payload.apiPb);

    const apiId = getV2ApiId(payload);
    state.entities = { [apiId]: payload };
    delete state.loading[ROOT];
    state.meta[apiId] = state.meta[apiId] ?? {};
    state.meta[apiId].needsBindingExtraction = true;
  },
  error(state, { payload }) {
    state.errors[ROOT] = { error: payload };
    delete state.loading[ROOT];
  },
});
